import React, {ReactNode, useState} from 'react';

import {browserHistory, withRouter, WithRouterProps} from 'react-router';
import {v4} from 'uuid';

import {createFormErrorsContext} from 'app/general/forms';
import {NewDocumentMetadata} from 'app/general/structures/NewDocumentMetadata';
import {SPPage} from 'app/general/structures/SPPage';
import {useWsOpenaiMessage} from 'app/openai/hooks';
import {AI_POLICY_PROMPT, AI_PROCEDURE_PROMPT} from 'app/openai/utils';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';
import {getPathFromURL} from 'app/utils/urls';

import type {FormErrorsProps} from 'app/general/forms';
import type {CreatableDocType, CreateNewDocProps} from 'app/general/types';
import type {FolderSerializedType, TeamSerializedType} from 'sweet-types';

interface Props
  extends FormErrorsProps,
    WithRouterProps<{
      name?: string;
      folder?: string;
    }> {
  elementName: 'procedure' | 'proceduregroup' | 'policy' | 'quiz';
  createNewDocument: (props: CreateNewDocProps) => Promise<CreatableDocType>;
  advancedContent?: ReactNode;
  disableFolder?: boolean;
  forceDisableConfirm?: boolean;
  disabledTeamText?: (team: TeamSerializedType) => string | undefined;
  disabledTooltip?: string;
  breadcrumbProps?: Record<string, any>;
}

function BaseGenericNewElementPage({
  createNewDocument,
  captureFormErrorsFromRejection,
  elementName,
  advancedContent,
  disableFolder,
  disabledTooltip,
  disabledTeamText,
  forceDisableConfirm,
  location,
  breadcrumbProps,
}: Props) {
  const [teams, setTeams] = useState<Array<TeamSerializedType>>([]);
  const [folders, setFolders] = useState<Array<FolderSerializedType>>([]);
  const [threadId] = useState(v4());

  const {sendMessage} = useWsOpenaiMessage({
    thread_id: threadId,
  });

  function goToEditUrl(url: string, suffix = '') {
    browserHistory.push(`${getPathFromURL(url)}edit/${suffix}`);
  }

  function redirectToStreamingEditPage({
    prompt,
    createPromise,
  }: {
    prompt: string;
    createPromise: Promise<CreatableDocType>;
  }) {
    function askForStreamingAI(element) {
      sendMessage(
        prompt,
        element.id,
        element.content_type,
        `edit_${element.content_type}_chat`
      );
      goToEditUrl(element.current_version.html_url, `?threadid=${threadId}`);
    }

    return createPromise.then(askForStreamingAI, (err) => {
      captureFormErrorsFromRejection(err);
      throw err;
    });
  }

  function redirectToEditPage(createPromise: Promise<CreatableDocType>) {
    return createPromise.then(
      (element) => goToEditUrl(element.current_version.html_url),
      (err) => {
        captureFormErrorsFromRejection(err);
        throw err;
      }
    );
  }

  function handleCreate(name: string, generateContent: boolean) {
    const teamsURLs = teams.map((team) => team.url);
    const foldersURLs = folders.map((folder) => folder.url);
    const promise = createNewDocument({
      name,
      teams: teamsURLs,
      folders: foldersURLs,
    });

    if (
      (elementName === 'procedure' || elementName === 'policy') &&
      generateContent
    ) {
      return redirectToStreamingEditPage({
        prompt:
          elementName === 'policy' ? AI_POLICY_PROMPT : AI_PROCEDURE_PROMPT,
        createPromise: promise,
      });
    } else {
      return redirectToEditPage(promise);
    }
  }

  return (
    <SPPage
      horizontalPadding={false}
      pageTitle={gt('Create %(name)s', {
        name: capitalizeFirstLetter(getContentType(elementName).name),
      })}
      breadcrumbProps={{...breadcrumbProps, ct: elementName}}
    >
      {({isSuperManager}) => (
        <NewDocumentMetadata
          defaultName={location.query.name}
          initialFolder={location.query.folder}
          ct={elementName}
          onCreate={handleCreate}
          forceDisableConfirm={
            (!isSuperManager && !teams.length) || !!forceDisableConfirm
          }
          disabledTooltip={
            disabledTooltip
              ? disabledTooltip
              : !isSuperManager && !teams.length
              ? gt(
                  'Please select at least one team to add this %s to',
                  elementName
                )
              : undefined
          }
          disabledTeamText={disabledTeamText}
          advancedContent={advancedContent}
          teams={teams}
          disableFolder={disableFolder}
          onChangeTeams={(teams) => Promise.resolve(setTeams(teams))}
          onChangeFolders={(folders) => Promise.resolve(setFolders(folders))}
        />
      )}
    </SPPage>
  );
}

export const GenericNewElementPage = createFormErrorsContext(
  withRouter(BaseGenericNewElementPage)
);
